import {
  FlexibleConnectedPositionStrategy,
  HorizontalConnectionPos,
  Overlay,
  OverlayConfig,
  OverlayRef,
  ScrollStrategyOptions,
  VerticalConnectionPos,
} from '@angular/cdk/overlay';
import { Platform } from '@angular/cdk/platform';
import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewContainerRef,
  ChangeDetectorRef,
} from '@angular/core';
import { PopoverComponent } from './popover.component';
import { Subject, Subscription, fromEvent, merge } from 'rxjs';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  mapTo,
  takeUntil,
} from 'rxjs/operators';

@Directive({
  selector: '[fxPopoverTriggerFor]',
  host: {
    "aria-haspopup": "true",
    class: "fx-popover-trigger",
  },
  exportAs: "fxPopoverTrigger",
})
export class PopoverTriggerDirective implements AfterViewInit, OnDestroy {
  @Input("fxPopoverTriggerFor")
  get popover(): PopoverComponent {
    return this._popover;
  }
  set popover(popover: PopoverComponent) {
    if (!popover || popover === this._popover) {
      return;
    }
    this._popover = popover;
    this._popoverClose$.unsubscribe();
    if (popover) {
      this._popoverClose$ = popover.closed.subscribe(() => {
        this._destroyPopover();
      });
    }
  }
  private _popoverClose$ = Subscription.EMPTY;
  private _popover: PopoverComponent;

  @Input("fxPopoverDisabled")
  disabled: boolean = false;

  @Input("fxPopoverData")
  popoverData: any;

  private _overlayRef: OverlayRef;
  private _portal: TemplatePortal;
  private _popoverOpen: boolean = false;
  private destroy$ = new Subject<any>();

  @Output()
  popoverOpened: EventEmitter<void> = new EventEmitter();
  @Output()
  popoverClosed: EventEmitter<void> = new EventEmitter();

  constructor(
    private _overlay: Overlay,
    private _element: ElementRef<HTMLElement>,
    private _ngZone: NgZone,
    private viewContainerRef: ViewContainerRef,
    private elementRef: ElementRef,
    private platform: Platform,
    private _changeDetectorRef: ChangeDetectorRef
  ) {}

  ngAfterViewInit(): void {
    if (this.popover) {
      const nativeElement = this.elementRef.nativeElement;
      const hostMouseState$ = merge(
        fromEvent(nativeElement, 'mouseenter').pipe(mapTo(true)),
        fromEvent(nativeElement, 'mouseleave').pipe(mapTo(false))
      );

      const popoverMouseState$ = this.popover.mouseState$.pipe(
        filter((v) => !this.popover.trigger || this.popover.trigger == this)
      );

      const mergedMouseState$ = merge(popoverMouseState$, hostMouseState$);

      mergedMouseState$
        .pipe(
          debounceTime(100),
          distinctUntilChanged(),
          filter(() => this.platform.isBrowser),
          takeUntil(this.destroy$)
        )
        .subscribe((visible) => {
          if (visible && !this.disabled) {
            this.openPopover();
          } else {
            this.closePopover();
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this._overlayRef) {
      this._overlayRef.dispose();
      this._overlayRef = null;
    }
  }

  openPopover() {
    if (this._popoverOpen) {
      return;
    }
    const overlayRef = this._createOverlay(this.popover);
    const overlayConfig = overlayRef.getConfig();
    const positionStrategy =
      overlayConfig.positionStrategy as FlexibleConnectedPositionStrategy;
    this._setPosition(this.popover, positionStrategy);
    overlayConfig.hasBackdrop = false;
    overlayRef.attach(this._getPortal(this.popover));
    this.popover.trigger = this;
    this._setIsPopoverOpen(true);
    this.popover.opened.next(this.popoverData);
  }

  closePopover() {
    this.popover.closed.emit();
  }

  private _setIsPopoverOpen(isOpen: boolean): void {
    if (isOpen !== this._popoverOpen) {
      this._popoverOpen = isOpen;
      this._popoverOpen ? this.popoverOpened.emit() : this.popoverClosed.emit();

      this._changeDetectorRef.markForCheck();
    }
  }

  private _setPosition(
    popover: PopoverComponent,
    positionStrategy: FlexibleConnectedPositionStrategy
  ) {
    let [originX, originFallbackX]: HorizontalConnectionPos[] =
      popover.xPosition === 'before' ? ['end', 'start'] : ['start', 'end'];

    let [overlayY, overlayFallbackY]: VerticalConnectionPos[] =
      popover.yPosition === 'above' ? ['bottom', 'top'] : ['top', 'bottom'];

    let [originY, originFallbackY] = [overlayY, overlayFallbackY];
    let [overlayX, overlayFallbackX] = [originX, originFallbackX];
    let offsetY = -32;

    positionStrategy.withPositions([
      { originX, originY, overlayX, overlayY, offsetY },
      {
        originX: originFallbackX,
        originY,
        overlayX: overlayFallbackX,
        overlayY,
        offsetY,
      },
      {
        originX,
        originY: originFallbackY,
        overlayX,
        overlayY: overlayFallbackY,
        offsetY: -offsetY,
      },
      {
        originX: originFallbackX,
        originY: originFallbackY,
        overlayX: overlayFallbackX,
        overlayY: overlayFallbackY,
        offsetY: -offsetY,
      },
    ]);
  }

  private _getPortal(popover: PopoverComponent): TemplatePortal {
    if (
      !this._portal 
      || this._portal.templateRef !== popover.templateRef
      || this._portal.context?.data !== this.popoverData
    ) {
      this._portal = new TemplatePortal(
        popover.templateRef,
        this.viewContainerRef,
        {
          data: this.popoverData
        }
      );
    }

    return this._portal;
  }

  private _createOverlay(popover: PopoverComponent): OverlayRef {
    if (!this._overlayRef) {
      const config = this._getOverlayConfig();
      this._subscribeToPositions(
        popover,
        config.positionStrategy as FlexibleConnectedPositionStrategy
      );
      this._overlayRef = this._overlay.create(config);
      this._overlayRef.keydownEvents().subscribe();
    }
    return this._overlayRef;
  }

  private _subscribeToPositions(
    popover: PopoverComponent,
    position: FlexibleConnectedPositionStrategy
  ) {
    if (popover.setPositionClasses) {
      position.positionChanges.subscribe((change) => {
        const posX =
          change.connectionPair.overlayX === 'start' ? 'after' : 'before';
        const posY =
          change.connectionPair.overlayY === 'top' ? 'below' : 'above';

        if (this._ngZone) {
          this._ngZone.run(() => popover.setPositionClasses!(posX, posY));
        } else {
          popover.setPositionClasses!(posX, posY);
        }

        const originElement: HTMLElement = this.elementRef.nativeElement;
        const overlayElement: HTMLElement = (
          this.viewContainerRef.get(0) as any
        )?.rootNodes[0];
        if (originElement && overlayElement) {
          const originRect = originElement.getBoundingClientRect();
          const overlayRect = overlayElement.getBoundingClientRect();
          const originLeft = originRect.left + originRect.width / 2;
          const overlayLeft = overlayRect.left + overlayRect.width / 2;

          const offset = originLeft - overlayLeft;

          this.popover.addArrowTranslateX(offset);
        }
      });
    }
  }

  private _getOverlayConfig() {
    return new OverlayConfig({
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(this._element)
        .withLockedPosition()
        .withGrowAfterOpen()
        .withTransformOriginOn('.fx-popover-panel'),
      backdropClass: "cdk-overlay-transparent-backdrop",
      panelClass: this.popover.panelClass,
      width: this.popover.width,
      minWidth: this.popover.minWidth,
      maxWidth: this.popover.maxWidth,
      height: this.popover.height,
      minHeight: this.popover.minHeight,
      maxHeight: this.popover.maxHeight
    });
  }

  private _destroyPopover() {
    if (!this._overlayRef || !this._popoverOpen) {
      return;
    }
    this._overlayRef.detach();
    this._setIsPopoverOpen(false);
  };
}
